/**
 * Entity workbench — Images tab (ENTITY_WORKBENCH_PLAN.md W4a).
 *
 * Every image we hold for this entity, in the workbench rather than in a
 * filtered corner of the library. Reads `GET /images?entity_id=…`, which
 * already does the hard part server-side: assayer entity → cynapse
 * global_entity, OR-matched against every announcement this entity owns, so
 * duplicate cynapse rows for the same company can't hide an image.
 *
 * The tile and the detail drawer are the library's own components — one
 * rendering of the six-role model and the hero/body eligibility reasons, not
 * a second that drifts. Retag / reclassify / soft-delete come with the drawer
 * and are the same operator-gated actions as on /images.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Image as ImageIcon, Loader2 } from 'lucide-react';
import { listImages } from '../../lib/api/images.js';
import type { ImageRole } from '../../lib/api/types.js';
import { ImageCard, toLightboxItem } from '../images/ImageCard.js';
import { ImageDetailDrawer } from '../images/ImageDetailDrawer.js';
import { ImageLightbox } from '../images/ImageLightbox.js';

const PAGE_SIZE = 48;

// Role filter chips. The six-role model is the single placement signal, so
// these are the cuts an operator actually reasons in: what could be a hero,
// what can only sit in the body, what is excluded from articles entirely.
const ROLE_FILTERS: { key: string; label: string; roles: ImageRole[] | null }[] = [
  { key: 'all', label: 'All', roles: null },
  { key: 'editorial_photo', label: 'Photos', roles: ['editorial_photo'] },
  { key: 'data_figure', label: 'Figures', roles: ['data_figure'] },
  { key: 'other', label: 'Logos & decorative', roles: ['logo', 'decorative'] },
];

export function ImagesTab({ entityId }: { entityId: string }) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [offset, setOffset] = useState(0);

  const roles = ROLE_FILTERS.find(f => f.key === roleFilter)?.roles ?? undefined;

  const imagesQuery = useQuery({
    queryKey: ['entity-images', entityId, roleFilter, offset],
    queryFn: () => listImages({
      entity_id: entityId,
      image_role: roles,
      limit: PAGE_SIZE,
      offset,
    }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['entity-images', entityId] });
  };

  const data = imagesQuery.data;
  const images = data?.images ?? [];
  const total = data?.total ?? 0;
  // The endpoint returns this when the entity has neither a cynapse binding
  // nor any announcements — "we can't look" is a different answer from
  // "we looked and hold none", and the operator needs to tell them apart.
  const unresolved = (data as { unresolved_entity_id?: string } | undefined)?.unresolved_entity_id;

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="text-sm font-semibold text-grey-900 flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-grey-400" />
            Images
            <span className="font-normal text-grey-400">
              {imagesQuery.isLoading
                ? '— loading…'
                : `— ${total} held${roleFilter === 'all' ? '' : ' in this cut'}`}
            </span>
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              {ROLE_FILTERS.map(f => (
                <button
                  key={f.key}
                  onClick={() => { setRoleFilter(f.key); setOffset(0); }}
                  className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors ${
                    roleFilter === f.key
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-grey-500 hover:bg-grey-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Link
              to={`/images?entity_id=${entityId}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              title="Full filter set, plus MarketIndex backlog sourcing for this entity"
            >
              Open in Images library <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {imagesQuery.isLoading && (
          <div className="py-12 flex items-center justify-center text-sm text-grey-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading images…
          </div>
        )}

        {imagesQuery.isError && (
          <p className="py-12 text-center text-sm text-red-600">Could not load images.</p>
        )}

        {unresolved && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              This entity has no Cynapse binding and no announcements held, so images
              cannot be looked up for it — this is not the same as holding none.
              Bind it on the Identity &amp; data quality tab first.
            </span>
          </div>
        )}

        {!imagesQuery.isLoading && !imagesQuery.isError && !unresolved && images.length === 0 && (
          <p className="py-12 text-center text-sm text-grey-400">
            No images held for this entity{roleFilter === 'all' ? '' : ' in this cut'}.
          </p>
        )}

        {images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {images.map((image, i) => (
              <ImageCard
                key={image.image_id}
                image={image}
                onClick={() => setSelectedId(image.image_id)}
                onZoom={() => setZoomIndex(i)}
              />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3 mt-4 text-sm">
            <button
              onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
              disabled={offset <= 0}
              className="px-3 py-1 border border-grey-200 rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-grey-600">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <button
              onClick={() => setOffset(o => o + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="px-3 py-1 border border-grey-200 rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </section>

      {zoomIndex !== null && images[zoomIndex] && (
        <ImageLightbox
          items={images.map(toLightboxItem)}
          index={zoomIndex}
          onIndex={setZoomIndex}
          onClose={() => setZoomIndex(null)}
        />
      )}

      {selectedId && (
        <ImageDetailDrawer
          imageId={selectedId}
          onClose={() => setSelectedId(null)}
          onChange={invalidate}
          onDeleted={() => { setSelectedId(null); invalidate(); }}
        />
      )}
    </div>
  );
}
